import React, { useState } from 'react';
import { Button, useNotify, useRefresh, useRedirect, useDataProvider } from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { apiRequest } from './utils';

const DEFAULT_RESOURCE = 'advertiser_ip_lists';

/**
 * Delete with a consequence-bearing confirmation.
 *
 * Deleting an IP list removes that targeting from every campaign referencing
 * it - the backend cascades those references off before deleting the list.
 * For a campaign that used the list to EXCLUDE traffic, that means it starts
 * serving to those IPs. A plain "are you sure?" would hide that, so this
 * dialog fetches GET /:resource/:code/usage first and names the affected
 * campaigns.
 *
 * Used by both the advertiser IP list screen (src/advertiser/ip_lists.js,
 * resource="advertiser_ip_lists") and the ops IP list screen
 * (src/ipLists.js, resource="ip_lists") - hence the `resource` prop rather
 * than two copies of this component.
 */
export const IpListDeleteButton = ({ record, resource = DEFAULT_RESOURCE, basePath }) => {
  const [usage, setUsage] = useState(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const open = async () => {
    try {
      const body = await apiRequest(`/${resource}/${record.code}/usage`);
      setUsage((body && body.campaigns) || []);
    } catch (err) {
      notify(`Could not check which campaigns use this list: ${err.message}`, 'warning');
    }
  };

  const confirm = async () => {
    try {
      await dataProvider.delete(resource, { id: record.code });
      notify('IP list deleted', 'info');
      setUsage(null);
      refresh();
      if (basePath) redirect('list', basePath);
    } catch (err) {
      notify(err.message || 'Delete failed', 'warning');
    }
  };

  if (!record || !record.code) return null;
  return (
    <>
      <Button label="Delete" onClick={open} startIcon={<DeleteIcon />} />
      <Dialog open={usage !== null} onClose={() => setUsage(null)}>
        <DialogTitle>Delete "{record.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {usage && usage.length > 0
              ? `${usage.length} campaign${usage.length === 1 ? '' : 's'} target this list ` +
                `(${usage.map(c => c.name || c.code).join(', ')}). Deleting it removes that ` +
                'targeting. Campaigns that used it to EXCLUDE traffic will start serving to those IPs.'
              : 'No campaigns target this list. Deleting it affects nothing else.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setUsage(null)} />
          <Button label="Delete" onClick={confirm} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IpListDeleteButton;
